import React from 'react';

// ─── Icons ────────────────────────────────────────────────────────────────────

const CreditCardIcon = ({ className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className={className}>
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const LockIcon = ({ className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className={className}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const SpinnerIcon = ({ className = '' }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 00-8 8h4z" />
  </svg>
);

const PayPalIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor">
    <path d="M20.067 8.478c.492.88.556 1.937.3 3.228-1.495 6.342-9.23 6.342-9.23 6.342H8.122l-1.244 6.342H3.657l.887-3.883L2.5 20.807h4.596l1.13-5.565h2.91c.428 0 3.176-.136 4.39-2.55.71-1.498.57-2.63 0-3.42-.242-.253-.62-.627-1.21-.794z" />
    <path d="M7.405 8.474c.643-2.87 3.506-2.87 3.506-2.87h6.16c.65 0 1.295.2 1.585.697.3.568.197 1.355.02 2.196-.62 3-3.13 2.87-3.13 2.87H12.11l-.76 3.57H8.122l1.007-4.88-.887-1.583h-.837z" />
  </svg>
);

// ─── Payment option row ───────────────────────────────────────────────────────

const PaymentOption = ({ id, active, disabled, onSelect, icon, title, subtitle }) => (
  <div
    onClick={() => !disabled && onSelect(id)}
    className={`flex items-center gap-3 border rounded-xl p-3 sm:p-4 transition-colors ${
      active    ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
    } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <div className={`flex-shrink-0 ${active ? 'text-indigo-600' : 'text-gray-500'}`}>
      {icon}
    </div>
    <div className="min-w-0">
      <p className="font-medium text-sm sm:text-base">{title}</p>
      <p className="text-xs sm:text-sm text-gray-500">{subtitle}</p>
    </div>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

const PaymentMethods = ({
  activePayment,
  onSelectPayment,
  selectedCount,
  onCheckout,
  isCheckingOut = false,
}) => {
  const isDisabled = selectedCount === 0 || isCheckingOut;

  const checkoutLabel = isCheckingOut
    ? 'מעבד את התשלום...'
    : selectedCount > 0
    ? `השלם רכישת ${selectedCount} שיעורים`
    : 'נא לבחור שיעורים לרכישה';

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 sm:p-5 lg:p-6">

      <h3 className="text-base sm:text-lg font-semibold text-indigo-800 mb-3 sm:mb-4">
        בחר אופן תשלום
      </h3>

      {/* Payment options */}
      <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-5">
        <PaymentOption
          id="credit"
          active={activePayment === 'credit'}
          disabled={isCheckingOut}
          onSelect={onSelectPayment}
          icon={<CreditCardIcon className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="תשלום בכרטיס אשראי"
          subtitle="Visa, Mastercard, Amex, Discover"
        />
        <PaymentOption
          id="paypal"
          active={activePayment === 'paypal'}
          disabled={isCheckingOut}
          onSelect={onSelectPayment}
          icon={<PayPalIcon />}
          title="תשלום באמצעות PayPal"
          subtitle="מהיר ומאובטח"
        />
      </div>

      {/* Checkout button */}
      <button
        type="button"
        disabled={isDisabled}
        onClick={onCheckout}
        className={`w-full inline-flex items-center justify-center gap-2 py-2.5 sm:py-3 rounded-xl font-bold text-sm sm:text-base transition-all ${
          !isDisabled
            ? 'bg-gradient-to-l from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 hover:shadow-lg hover:-translate-y-0.5'
            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
        }`}
      >
        {isCheckingOut && <SpinnerIcon className="animate-spin w-4 h-4 sm:w-5 sm:h-5" />}
        <span>{checkoutLabel}</span>
      </button>

      {/* SSL note */}
      <div className="flex items-center justify-center gap-1.5 mt-3 text-gray-400 text-xs sm:text-sm">
        <LockIcon className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
        <span>התשלום מאובטח ומוצפן בתקן SSL</span>
      </div>
    </div>
  );
};

export default PaymentMethods;